<template>
  <div class="flex flex-col">
    <s-header class="sticky top-0 z-50 pb-4">
      <template #default>
        <span class="leading-none overflow-visible">Tasks</span>
      </template>
      <template #sub-heading>
        <div class="ml-2">
          <div class="dropdown-container relative">
            <!-- Shift selector dropdown trigger -->
            <div
              @click.stop="toggleShiftSelector"
              class="flex items-center cursor-pointer group"
            >
              <span class="text-fire">
                {{ baseLabel }}
                <span
                  v-if="shiftHours"
                  class="text-gray-600 text-xl sm:text-2xl ml-2"
                >
                  ({{ shiftHours }})
                </span>
                <span
                  v-else-if="dueDate"
                  class="text-gray-600 text-xl sm:text-2xl ml-2"
                >
                  ({{ dueDate }})
                </span>
              </span>
              <unicon
                name="angle-down"
                class="flex-shrink-0 w-8 h-8 ml-2 text-gray-500 transition-transform duration-200"
                :class="isShiftSelectorOpen ? 'transform rotate-180' : ''"
              />
            </div>

            <!-- Shift selector dropdown -->
            <transition name="fade">
              <div
                v-if="isShiftSelectorOpen"
                class="absolute z-50 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 w-96 dropdown-max-width max-h-screen overflow-y-auto overscroll-contain"
                @click.stop
                style="-webkit-overflow-scrolling: touch; max-height: 80vh"
              >
                <!-- Daily Day Parts -->
                <div class="p-3 border-b border-gray-200">
                  <h3
                    class="text-sm font-semibold text-gray-500 uppercase mb-2 sticky-header"
                  >
                    Daily Day Parts
                  </h3>
                  <div class="space-y-2">
                    <div
                      v-for="(shiftName, index) in shifts"
                      :key="`shift_${index}`"
                      @click="selectShift(index)"
                      class="p-2 rounded-md cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                      :class="[
                        shift === index ? 'bg-red-50' : '',
                        index === currentShift ? 'bg-blue-50' : ''
                      ]"
                    >
                      <div class="flex items-center justify-between w-full">
                        <div class="flex items-center">
                          <span
                            class="font-medium"
                            :class="[
                              shift === index
                                ? 'font-bold'
                                : index < currentShift
                                ? 'text-gray-400'
                                : 'text-gray-800'
                            ]"
                          >
                            {{ shiftName | capitalize }}
                            <span
                              v-if="getShiftHours(index)"
                              class="ml-2 text-sm"
                              :class="
                                index === currentShift
                                  ? 'text-blue-500'
                                  : index < currentShift
                                  ? 'text-gray-400'
                                  : 'text-gray-500'
                              "
                            >
                              <unicon
                                v-if="index === currentShift"
                                name="angle-right"
                                class="-mb-2 -mr-2 w-6 h-6 text-blue-500 text-sm"
                                fill="currentColor"
                              />
                              ({{ getShiftHours(index) }})
                            </span>
                          </span>
                        </div>
                        <span
                          v-if="
                            tasksRemainingByShifts &&
                            tasksRemainingByShifts[index] &&
                            tasksRemainingByShifts[index].length > 0
                          "
                          class="px-2 py-1 text-xs font-medium rounded-full"
                          :class="
                            index < currentShift
                              ? 'bg-fire text-white'
                              : index === currentShift
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-200 text-gray-700'
                          "
                        >
                          {{ tasksRemainingByShifts[index].length }}
                        </span>
                      </div>
                      <div
                        class="w-full h-2 bg-gray-200 rounded-full overflow-hidden mt-2"
                      >
                        <div
                          class="h-full transition-all duration-300 ease-out"
                          :class="
                            index < currentShift ? 'bg-gray-400' : 'bg-blue-500'
                          "
                          :style="`width: ${
                            tasksPercentCompletedByShifts[index] || 0
                          }%`"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Weekly and Monthly -->
                <div class="p-3">
                  <h3
                    class="text-sm font-semibold text-gray-500 uppercase mb-2"
                  >
                    Recurring Tasks
                  </h3>
                  <div class="space-y-2">
                    <div
                      @click="selectRecurringType('weekly')"
                      class="p-2 rounded-md cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                      :class="log && log.type === 'weekly' ? 'bg-red-50' : ''"
                    >
                      <div class="flex items-center justify-between w-full">
                        <div class="flex flex-col">
                          <span
                            class="font-medium text-gray-800"
                            :class="
                              log && log.type === 'weekly' ? 'font-bold' : ''
                            "
                          >
                            Weekly
                          </span>
                          <span class="text-sm text-gray-500">
                            Due in {{ weeklyDueDays }} days
                          </span>
                        </div>
                        <span
                          v-if="
                            tasksRemaining &&
                            tasksRemaining.weekly &&
                            tasksRemaining.weekly.length > 0
                          "
                          class="px-2 py-1 text-xs font-medium rounded-full bg-gray-200 text-gray-700"
                        >
                          {{ tasksRemaining.weekly.length }}
                        </span>
                      </div>
                      <div
                        class="w-full h-2 bg-gray-200 rounded-full overflow-hidden mt-2"
                      >
                        <div
                          class="h-full bg-gray-600 transition-all duration-300 ease-out"
                          :style="`width: ${weeklyTasksPercentCompleted}%`"
                        ></div>
                      </div>
                    </div>

                    <div
                      @click="selectRecurringType('monthly')"
                      class="p-2 rounded-md cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                      :class="log && log.type === 'monthly' ? 'bg-red-50' : ''"
                    >
                      <div class="flex items-center justify-between w-full">
                        <div class="flex flex-col">
                          <span
                            class="font-medium"
                            :class="
                              log && log.type === 'monthly' ? 'font-bold' : ''
                            "
                          >
                            Monthly
                          </span>
                          <span class="text-sm text-gray-500">
                            Due in {{ monthlyDueDays }} days
                          </span>
                        </div>
                        <span
                          v-if="
                            tasksRemaining &&
                            tasksRemaining.monthly &&
                            tasksRemaining.monthly.length > 0
                          "
                          class="px-2 py-1 text-xs font-medium rounded-full bg-gray-200 text-gray-700"
                        >
                          {{ tasksRemaining.monthly.length }}
                        </span>
                      </div>
                      <div
                        class="w-full h-2 bg-gray-200 rounded-full overflow-hidden mt-2"
                      >
                        <div
                          class="h-full bg-gray-700 transition-all duration-300 ease-out"
                          :style="`width: ${monthlyTasksPercentCompleted}%`"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>
          <div class="mt-1">
            <div class="flex items-center">
              <div class="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  class="h-full bg-gray-600 transition-all duration-300 ease-out"
                  :style="`width: ${tasksPercentCompleted}%`"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </s-header>

    <div class="mt-4">
      <task-item
        v-for="id of tasksList"
        :key="id"
        :id="id"
        :log="log"
        class="mb-1 font-semibold text-xl"
        @putValue="putValue({ id, value: $event })"
      />
    </div>
  </div>
</template>

<script>
import SHeader from '@/components/SHeader'
import { mapGetters, mapActions } from 'vuex'
import TaskItem from '@/components/TaskItem.vue'
import { DateTime } from 'luxon'

export default {
  name: 'TaskList',
  props: {
    shift: {
      type: Number,
      required: false,
      default: null
    },
    log: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      isShiftSelectorOpen: false
    }
  },
  components: {
    TaskItem,
    SHeader
  },
  computed: {
    ...mapGetters('storeModule', [
      'tasksByShift',
      'tasksTodayByShifts',
      'shifts',
      'tasksPercentCompletedByShifts',
      'hoursByDay',
      'tasksRemaining',
      'tasksRemainingByShifts'
    ]),
    ...mapGetters('global', ['weeklyTasks', 'monthlyTasks']),
    ...mapGetters('user', ['id', 'name']),
    ...mapGetters(['currentShift']),
    tasksList() {
      // If no shift is specified (null or -1), we're in weekly/monthly view
      if (this.shift === null || this.shift === -1) {
        // Get tasks from the appropriate log
        if (!this.log) {
          console.log('No log provided')
          return []
        }

        console.log('Current log:', this.log)
        // Get tasks based on log type
        const tasks =
          this.log.type === 'weekly' ? this.weeklyTasks : this.monthlyTasks
        console.log('Tasks from global store:', tasks)

        // Sort tasks by index if they have one, otherwise maintain original order
        return Object.keys(tasks).sort((a, b) => {
          const taskA = tasks[a]
          const taskB = tasks[b]
          const indexA =
            taskA.index !== undefined ? taskA.index : Number.MAX_SAFE_INTEGER
          const indexB =
            taskB.index !== undefined ? taskB.index : Number.MAX_SAFE_INTEGER
          return indexA - indexB
        })
      }

      // Otherwise, we're in daily view with shifts
      return this.tasksTodayByShifts && this.tasksTodayByShifts[this.shift]
        ? this.tasksTodayByShifts[this.shift]
        : []
    },
    baseLabel() {
      if (this.shift === null || this.shift === -1) {
        const type = this.log && this.log.type
        return type ? type.charAt(0).toUpperCase() + type.slice(1) : ''
      }
      return this.shifts && this.shifts[this.shift]
        ? this.shifts[this.shift].charAt(0).toUpperCase() +
            this.shifts[this.shift].slice(1)
        : ''
    },
    dueDate() {
      if (!this.log || !this.log.type) return null

      if (this.log.type === 'weekly') {
        return `Due by ${DateTime.now()
          .setZone('America/New_York')
          .endOf('week')
          .toFormat('EEEE')}`
      } else if (this.log.type === 'monthly') {
        return DateTime.now().setZone('America/New_York').toFormat('MMMM')
      }
      return null
    },
    shiftHours() {
      if (this.shift === null || this.shift === -1) return null

      const today = DateTime.now().setZone('America/New_York').weekday - 1 // Convert to 0-6
      const hours = this.hoursByDay(today)

      if (!hours || !hours[this.shift]) return null

      const start = this.formatHour(hours[this.shift])
      // For the final shift, use midnight (12AM) as the end time
      const end = hours[this.shift + 1]
        ? this.formatHour(hours[this.shift + 1])
        : '12AM'
      return `${start} - ${end}`
    },
    tasksPercentCompleted() {
      if (this.shift === null || this.shift === -1) {
        // For weekly/monthly views, calculate completion percentage
        if (!this.log) return 0

        const tasks =
          this.log.type === 'weekly' ? this.weeklyTasks : this.monthlyTasks
        const totalTasks = Object.keys(tasks).length
        if (totalTasks === 0) return 0

        const completedTasks = Object.keys(tasks).filter(
          (taskId) => this.log[taskId] && this.log[taskId].completed
        ).length

        return Math.round((completedTasks / totalTasks) * 100)
      }

      // For daily tasks, use the existing percentage
      return this.tasksPercentCompletedByShifts[this.shift] || 0
    },
    weeklyDueDays() {
      const now = DateTime.now().setZone('America/New_York')
      const endOfWeek = now.endOf('week')
      const diff = endOfWeek.diff(now, 'days').days
      return Math.floor(diff)
    },
    monthlyDueDays() {
      const now = DateTime.now().setZone('America/New_York')
      const endOfMonth = now.endOf('month')
      const diff = endOfMonth.diff(now, 'days').days
      return Math.floor(diff)
    },
    weeklyTasksPercentCompleted() {
      // Calculate weekly tasks completion percentage
      const weeklyTasks = this.weeklyTasks || {}
      const totalTasks = Object.keys(weeklyTasks).length
      if (totalTasks === 0) return 0

      // Get the weekly log to check completion status
      const weeklyLog = this.$store.getters['storeModule/weeklyLog']
        ? this.$store.getters['storeModule/logs/logById'](
            this.$store.getters['storeModule/weeklyLog']
          )
        : null

      if (!weeklyLog) return 0

      const completedTasks = Object.keys(weeklyTasks).filter(
        (taskId) => weeklyLog[taskId] && weeklyLog[taskId].completed
      ).length

      return Math.round((completedTasks / totalTasks) * 100)
    },
    monthlyTasksPercentCompleted() {
      // Calculate monthly tasks completion percentage
      const monthlyTasks = this.monthlyTasks || {}
      const totalTasks = Object.keys(monthlyTasks).length
      if (totalTasks === 0) return 0

      // Get the monthly log to check completion status
      const monthlyLog = this.$store.getters['storeModule/monthlyLog']
        ? this.$store.getters['storeModule/logs/logById'](
            this.$store.getters['storeModule/monthlyLog']
          )
        : null

      if (!monthlyLog) return 0

      const completedTasks = Object.keys(monthlyTasks).filter(
        (taskId) => monthlyLog[taskId] && monthlyLog[taskId].completed
      ).length

      return Math.round((completedTasks / totalTasks) * 100)
    }
  },
  methods: {
    ...mapActions('storeModule', [
      'updateCurrentLogItem',
      'updateWeeklyLogItem',
      'updateMonthlyLogItem'
    ]),
    toggleShiftSelector() {
      this.isShiftSelectorOpen = !this.isShiftSelectorOpen
    },
    selectShift(shiftIndex) {
      // Close the dropdown
      this.isShiftSelectorOpen = false

      // Navigate to the selected shift
      this.$router.push(`/tasks/${this.shifts[shiftIndex]}`)
    },
    selectRecurringType(type) {
      // Close the dropdown
      this.isShiftSelectorOpen = false

      // Navigate to the selected recurring task type
      this.$router.push(`/tasks/${type}`)
    },
    getShiftHours(shiftIndex) {
      if (shiftIndex === null || shiftIndex === undefined) return null

      const today = DateTime.now().setZone('America/New_York').weekday - 1 // Convert to 0-6
      const hours = this.hoursByDay(today)

      if (!hours || !hours[shiftIndex]) return null

      const start = this.formatHour(hours[shiftIndex])
      // For the final shift, use midnight (12AM) as the end time
      const end = hours[shiftIndex + 1]
        ? this.formatHour(hours[shiftIndex + 1])
        : '12AM'
      return `${start} - ${end}`
    },
    putValue({ id, value = false }) {
      if (!id) return console.error
      try {
        // Create the update data with completion info
        const updateData = {
          [`${id}.completed`]: value
        }

        // If task is being completed, add timestamp and user info
        if (value) {
          updateData[`${id}.completedAt`] = new Date().toISOString()
          updateData[`${id}.completedBy`] = this.id
          updateData[`${id}.completedByName`] = this.name
        } else {
          // If task is being uncompleted, clear the completion info
          updateData[`${id}.completedAt`] = null
          updateData[`${id}.completedBy`] = null
        }

        // Determine which update action to use based on log type
        const updateAction =
          this.log && this.log.type === 'weekly'
            ? this.updateWeeklyLogItem
            : this.log && this.log.type === 'monthly'
            ? this.updateMonthlyLogItem
            : this.updateCurrentLogItem

        updateAction(updateData)
      } catch {
        console.error
      }
    },
    formatHour(hour) {
      if (typeof hour !== 'number') return ''
      const period = hour >= 12 ? 'PM' : 'AM'
      const displayHour = hour % 12 || 12
      return `${displayHour}${period}`
    }
  },
  mounted() {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (
        this.isShiftSelectorOpen &&
        !event.target.closest('.dropdown-container')
      ) {
        this.isShiftSelectorOpen = false
      }
    }

    document.addEventListener('click', handleClickOutside)

    this.$once('hook:beforeDestroy', () => {
      document.removeEventListener('click', handleClickOutside)
    })
  }
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.fade-leave-to,
.fade-enter {
  opacity: 0;
  transform: translateY(-10px);
}

/* Custom class for max-width on mobile */
.dropdown-max-width {
  max-width: 90vw;
}

/* Sticky header styling */
.sticky-header {
  position: sticky;
  top: 0;
  background-color: white;
  padding-top: 1px;
  padding-bottom: 1px;
  z-index: 1;
}

/* Mobile-friendly scrolling styles */
.dropdown-container .absolute.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.3) transparent;
}

.dropdown-container .absolute.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.dropdown-container .absolute.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.dropdown-container .absolute.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 3px;
}
</style>
